<template>
  <div
    class="animated-icon"
    :aria-label="ariaLabel || $string('accessibility.icon_default')"
    role="img"
  >
    <div
      class="animated-icon__wrapper"
      :class="[
        `animated-icon--${transitionType}`,
        { 'animated-icon--delayed' : delayTransition }
      ]"
      :tabindex=" focusable ? 0 : -1 "
      @mouseenter="handleHover"
      @mouseleave="handleHover"
      @click="handleClick"
      @keydown.enter="handleClick"
    >
      <div
        class="
          animated-icon__icon
          animated-icon__icon-one
        "
        :class="{ [$class('activeIcon')]: activeIcon === 'one' }"
      >
        <slot name="iconOne" />
      </div>
      <div
        class="
          animated-icon__icon
          animated-icon__icon-two
        "
        :class="{ [$class('activeIcon')]: activeIcon === 'two' }"
      >
        <slot name="iconTwo" />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Animated icon (animated-icon)
 * -----------------------------------------------------------------------------
 * Icon component that displays icons with varying transitions.
 *
 * @param {String} [ariaLabel] - String to add as aria label.
 * @param {String} [transitionType] - Type of transition to display, valid values are: 'swipe', 'swipe-right', 'spin-90', 'spin-180'.
 * @param {Boolean} [transitionOnHover] - Controls if the icon should transition on hover.
 *
 */
export default {
  name: 'AnimatedIcon',

  props: {
    ariaLabel: {
      type: String,
      default: '',
    },
    delayTransition: {
      type: Boolean,
      default: true,
    },
    transitionType: {
      type: String,
      default: 'swipe',
      validator(value) {
        return [
          'swipe', 'swipe-right', 'spin-90', 'spin-180',
        ].includes(value)
      },
    },
    transitionOnHover: {
      type: Boolean,
      default: true,
    },
    transitionByProp: {
      type: Boolean,
      default: false,
    },
    focusable: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['animated-icon-click'],

  data() {
    return {
      global: true,
      activeIcon: 'one',
    }
  },

  watch:{

    /**
     * Watch for the prop updating and transition the icon.
     */
    transitionByProp: function() {
      this.transitionIcon()
    },
  },

  mounted()  {
    if (!this.transitionByProp)  {
      return
    }

    this.transitionIcon()
  },

  methods: {

    /**
     * Handles the mouse hover event.
     */
    handleHover()  {
      if (!this.transitionOnHover)  {
        return
      }

      this.transitionIcon()
    },

    /**
     * Communicates a click event to a parent.
     */
    handleClick()  {
      this.$emit('animated-icon-click')
    },

    /**
     * Handles resetting the icons to default state.
     * @param {String} icon - Which is to be the active icon.
     */
    resetIcons(icon = 'one') {
      this.activeIcon = icon
    },

    /**
     * Handles the class changes to effect the transition.
     */
    transitionIcon(val) {
      if (val) {
        this.activeIcon = val
        return
      }

      this.activeIcon === 'one' ?
        this.activeIcon = 'two' :
        this.activeIcon = 'one'
    },
  },
}
</script>
